import * as React from 'react'
import { Link } from 'react-router-dom'
import { translateWithUserLocale } from '../../utils/i18n'

import CardMedia from './CardMedia'
import CreativeStatusIcon from '../CreativeStatusIcon'
import HistoryTag from '../HistoryTag/HistoryTag'
import {
  CreativeCard,
  SelectableCreativeCardState,
} from '../../interfaces/entities/cards'
import { Creative, CreativeAction } from '../../interfaces/entities/creative'
import CreativeActions from '../CreativeActions'
import getFormatIcon from './getFormatIcon'
import DI from '../../app/di'
import { ICreativeService } from '../../interfaces/services/creatives'
import { IFilesService } from '../../interfaces/services/files'

import { twoLineText } from '../../utils/string'
import { imageAsset, videoAsset } from '../../interfaces/entities/assets'
import defaultThumbnail from '../../assets/images/no-IMG.png'
import newImg from '../../assets/images/label_new_ribbon.png'

import {
  Card,
  CardHeader,
  CardContent,
  Icon,
  MenuItem,
  Tooltip,
  Box,
  styled,
  useTheme,
  Theme,
} from '@design/pt-ui'
type CreativeCardOptions = {
  showVideoInCarousel?: boolean
}

export const MakeCreativeCard = (
  creative: Creative,
  opts?: CreativeCardOptions,
): CreativeCard => {
  const options = opts || {}
  const { showVideoInCarousel = true } = options

  const creativeService = DI.get('CreativeService') as ICreativeService
  const filesService = DI.get('FilesService') as IFilesService

  const creativeVersion = creativeService.getCreativeVersion(creative)
  const defaultCard: CreativeCard = {
    creative,
    type: 'image',
    image: imageAsset(filesService.getFileThumbnailUrl(creative.data.file)),
  }

  try {
    const { data } = creative

    if (creativeVersion === 'video_1') {
      return {
        creative,
        type: 'video',
        thumbnail: imageAsset(
          filesService.getFileThumbnailUrl(data.thumbnail || data.file),
        ),
      }
    }

    if (creativeVersion === 'carousel_1') {
      return {
        creative,
        type: 'carousel',
        assets: data.creatives.map((creative: any) => {
          return imageAsset(
            filesService.getFileThumbnailUrl(
              creative.thumbnail || creative.file,
            ),
          )
        }),
      }
    }

    if (creativeVersion === 'carousel_2') {
      return {
        creative,
        type: 'carousel',
        assets: data.creatives.map((creative: any) => {
          if (creative.image != null && creative.video == null) {
            return imageAsset(filesService.getFileThumbnailUrl(creative.image))
          }

          if (creative.video != null) {
            if (showVideoInCarousel) {
              return videoAsset(
                filesService.getFileThumbnailUrl(
                  creative.image || creative.video,
                ),
              )
            } else {
              return imageAsset(
                filesService.getFileThumbnailUrl(
                  creative.image || creative.video,
                ),
              )
            }
          }

          return imageAsset(defaultThumbnail)
        }),
      }
    }
  } catch (e) {
    return defaultCard
  }

  return defaultCard
}

const Root = styled('div')({
  height: '100%',
  position: 'relative',
})

const NewTag = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: -15,
  top: theme.spacing(2.4),
}))

const StyledCard = styled(Card)({
  maxWidth: 200,
  height: '100%',
  paddingBottom: 5,
})

const StyledCardHeader = styled(CardHeader, {
  shouldForwardProp: (prop) => prop !== 'isNew',
})<{ isNew?: boolean }>(({ theme, isNew }) => ({
  paddingTop: theme.spacing(1),
  paddingLeft: isNew ? theme.spacing(2) : theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  '& .MuiCardHeader-avatar': {
    marginRight: theme.spacing(1),
    cursor: 'help',
  },
  '& .MuiCardHeader-action': {
    alignSelf: 'center',
    marginTop: 0,
  },
  '& .MuiCardHeader-content': {
    display: 'flex',
  },
}))

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(1),
  '&:hover': {
    cursor: 'pointer',
  },
  '&:last-child': {
    paddingBottom: theme.spacing(4),
  },
}))

const FormatRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  flexFlow: 'row',
  margin: '6px 0',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: 'grey',
  fontSize: 14,
})

const FormatIcon = styled(Icon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: '#666666',
}))

const StyledLink = styled(Link)({
  textDecoration: 'none',
  color: 'rgba(0, 0, 0, 0.87)',
})

const cardNameStyles = (theme: Theme) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  fontSize: '0.9rem',
  display: 'block',
  height: 'auto',
  color: 'rgba(0, 0, 0, 0.87)',
  fontWeight: 600,
})

const StyledMenuItem = styled(MenuItem)(({ theme }) =>
  cardNameStyles(theme),
) as typeof MenuItem

const CardName = styled('div')(({ theme }) => cardNameStyles(theme))

interface ICreativeCardProps {
  state?: SelectableCreativeCardState
  creative: Creative
  navigateOnClick: boolean
  doCreativeAction?: (creative: Creative, action: CreativeAction) => any
  linkActive?: boolean
  formatSetType?: string
}

interface ICreativeCardState {
  anchorEl: any
}

function CreativeCardComponent(props: ICreativeCardProps) {
  const theme = useTheme()
  const [state, setState] = React.useState<ICreativeCardState>({
    anchorEl: null,
  })

  const clearAnchorEl = React.useCallback(() => {
    setState({ anchorEl: null })
  }, [])

  const onRightClick = React.useCallback((event: React.SyntheticEvent) => {
    event.preventDefault()
    setState({ anchorEl: event.currentTarget })
  }, [])

  const onClick = React.useCallback(() => {
    const history = DI.get('history')
    if (props.creative) {
      history.push(`/creatives/${props.creative.id}`)
    }
  }, [props.creative])

  const voidClick = React.useCallback((e: any) => {
    e.preventDefault()
  }, [])

  const handleMediaClick = React.useCallback(() => {
    if (props.navigateOnClick) {
      const history = DI.get('history')
      history.push(`/creatives/${props.creative.id}`)
    }
  }, [props.navigateOnClick, props.creative])

  const { linkActive = true } = props

  let style = {}
  if (props.state === 'selected') {
    style = {
      boxShadow: `0px 0px 1px 2px ${theme.palette.custom.border.recommended}`,
    }
  } else if (props.state === 'disabled') {
    style = {
      opacity: '0.5',
    }
  } else if (
    props.state === 'over_recommended' &&
    props.formatSetType === 'facebook'
  ) {
    style = {
      boxShadow: `0px 0px 1px 2px ${theme.palette.custom.border.recommended}`,
    }
  } else if (props.state === 'dragging') {
    style = {
      boxShadow: `0px 0px 1px 2px ${theme.palette.custom.border.recommended}`,
    }
  }

  const isNew =
    props?.creative?.sent?.length === 0 &&
    props?.creative?.status?.name !== 'archived' &&
    props?.creative?.status?.name !== 'outdated'

  return (
    <Root onContextMenu={onRightClick}>
      {isNew && (
        <NewTag>
          <img style={{ width: '50%' }} src={newImg} alt="" />
        </NewTag>
      )}
      <StyledCard sx={style} raised={false}>
        <StyledCardHeader
          isNew={isNew}
          avatar={<CreativeStatusIcon status={props.creative.status} />}
          action={
            <div>
              {props.creative.status.name !== 'deleted' &&
              props.doCreativeAction ? (
                <CreativeActions
                  creative={props.creative}
                  anchorEl={state.anchorEl}
                  onMenuClose={clearAnchorEl}
                  doCreativeAction={props.doCreativeAction}
                  formatsetType={props.formatSetType}
                />
              ) : null}
            </div>
          }
          title={
            (props.creative.reopens !== undefined &&
              props.creative.reopens.length > 0) ||
            props.creative.sent.length > 0 ? (
              <>
                <HistoryTag
                  icon="local_offer"
                  iconColor="action"
                  events={props.creative.reopens}
                  title={translateWithUserLocale(
                    'history.reopened_after_sent_to_agency',
                  )}
                />
              </>
            ) : (
              ''
            )
          }
          subheader={
            props.creative.creativeMigration !== undefined ? (
              <Tooltip
                sx={{ marginLeft: '5px' }}
                title={props.creative.creativeMigration.message}>
                <Icon color="secondary">timelapse</Icon>
              </Tooltip>
            ) : (
              ''
            )
          }
        />
        <Tooltip
          title={
            props.state === 'over_recommended'
              ? props.formatSetType === 'facebook'
                ? translateWithUserLocale('warning.more_than_recommended')
                : ''
              : ''
          }
          placement="top">
          <Box>
            <CardMedia
              data={MakeCreativeCard(props.creative)}
              onClick={handleMediaClick}
              width={200}
              height={120}
            />
          </Box>
        </Tooltip>

        <StyledCardContent onClick={linkActive ? onClick : voidClick}>
          <FormatRow>
            <FormatIcon>{getFormatIcon(props.creative.type.name)}</FormatIcon>
            {props.creative.type.label}
          </FormatRow>

          {linkActive ? (
            <Tooltip
              disableFocusListener={true}
              disableTouchListener={true}
              title={props.creative.name}>
              <span>
                <StyledLink to={'/creatives/' + props.creative.id}>
                  <StyledMenuItem component="div">
                    {twoLineText(props.creative.name)}
                  </StyledMenuItem>
                </StyledLink>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={props.creative.name}>
              <CardName>{twoLineText(props.creative.name)}</CardName>
            </Tooltip>
          )}
        </StyledCardContent>
      </StyledCard>
    </Root>
  )
}

export default CreativeCardComponent
